import { useCallback, useEffect, useState } from "react";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatApiError } from "../../lib/utils-extra";

const NONE = "__none__";
const WEEKDAYS = [
  { key: "mon", label: "Mon" }, { key: "tue", label: "Tue" }, { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" }, { key: "fri", label: "Fri" }, { key: "sat", label: "Sat" }, { key: "sun", label: "Sun" },
];

/**
 * Schema-driven tenant-scoped CRUD section used by the Settings Engine.
 * `fields`: [{ name, label, type, required?, options?, allowEmpty?, emptyLabel?, placeholder?, default? }]
 *   types: 'text' | 'textarea' | 'select' | 'time' | 'date' | 'boolean' | 'weekdays'
 * Opt-in props:
 *   `body`        (item) => string   — extra description line shown under the subtitle
 *   `showInactive` boolean           — include archived rows and show an Active/Inactive status badge
 */
export function SettingsCrud({
  title, description, endpoint, fields, icon: Icon,
  subtitle, meta, body, testid, emptyText, singular: singularProp, showInactive,
}) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const defaultFor = (f) => {
    if (f.type === "weekdays") return f.default ?? [];
    if (f.type === "boolean") return f.default ?? false;
    if (f.type === "select" && f.allowEmpty) return NONE;
    return f.default ?? "";
  };
  const blank = () => Object.fromEntries(fields.map((f) => [f.name, defaultFor(f)]));
  const loadVal = (f, item) => {
    const v = item[f.name];
    if (f.type === "weekdays") return Array.isArray(v) ? v : [];
    if (f.type === "boolean") return !!v;
    if (f.type === "select" && f.allowEmpty) return v ? v : NONE;
    return v ?? (f.default ?? "");
  };

  const reload = useCallback(
    () => api.get(endpoint)
      .then(({ data }) => setList(showInactive ? (data || []) : (data || []).filter((x) => x.active !== false)))
      .catch(() => {}),
    [endpoint, showInactive],
  );
  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => { setEditId(null); setForm(blank()); setOpen(true); };
  const openEdit = (item) => {
    setEditId(item.id);
    setForm(Object.fromEntries(fields.map((f) => [f.name, loadVal(f, item)])));
    setOpen(true);
  };

  const toPayload = () => {
    const p = { ...form };
    for (const f of fields) {
      if (f.type === "select" && f.allowEmpty && p[f.name] === NONE) p[f.name] = null;
    }
    return p;
  };

  const save = async () => {
    for (const f of fields) {
      if (f.required && !String(form[f.name] || "").trim()) { toast.error(`${f.label} is required`); return; }
    }
    setBusy(true);
    try {
      if (editId) await api.put(`${endpoint}/${editId}`, toPayload());
      else await api.post(endpoint, toPayload());
      toast.success(editId ? `${singular} updated` : `${singular} added`);
      setOpen(false);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`${endpoint}/${del.id}`);
      toast.success("Removed");
      setDel(null); reload();
    } catch (e) { toast.error(formatApiError(e)); setDel(null); }
  };

  const singular = singularProp || title.replace(/s$/, "");

  const renderField = (f) => {
    const val = form[f.name];
    if (f.type === "boolean") {
      return (
        <div className="flex items-center gap-3 mt-1.5">
          <Switch checked={!!val} onCheckedChange={(v) => setForm({ ...form, [f.name]: v })} data-testid={`${testid}-field-${f.name}`} />
          <span className="text-sm text-[#475569]">{f.switchLabel || "Enabled"}</span>
        </div>
      );
    }
    if (f.type === "weekdays") {
      const arr = Array.isArray(val) ? val : [];
      return (
        <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`${testid}-field-${f.name}`}>
          {WEEKDAYS.map((d) => {
            const on = arr.includes(d.key);
            return (
              <button key={d.key} type="button"
                onClick={() => setForm({ ...form, [f.name]: on ? arr.filter((x) => x !== d.key) : [...arr, d.key] })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${on ? "bg-[#0EA5E9] text-white" : "bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]"}`}
                data-testid={`${testid}-day-${d.key}`}>
                {d.label}
              </button>
            );
          })}
        </div>
      );
    }
    if (f.type === "select") {
      return (
        <Select value={val || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
          <SelectTrigger className="mt-1.5" data-testid={`${testid}-field-${f.name}`}>
            <SelectValue placeholder={f.placeholder || "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {f.allowEmpty && <SelectItem value={NONE}>{f.emptyLabel || "None"}</SelectItem>}
            {(f.options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === "textarea") {
      return (
        <Textarea className="mt-1.5" rows={f.rows || 3} value={val || ""} placeholder={f.placeholder}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  data-testid={`${testid}-field-${f.name}`} />
      );
    }
    return (
      <Input className="mt-1.5" type={f.type === "time" ? "time" : f.type === "date" ? "date" : "text"}
             value={val || ""} placeholder={f.placeholder}
             onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
             data-testid={`${testid}-field-${f.name}`} />
    );
  };

  return (
    <div className="space-y-4" data-testid={`${testid}-section`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display text-xl font-semibold flex items-center gap-2">
            {Icon && <Icon size={20} weight="duotone" color="#0EA5E9" />} {title}
          </div>
          {description && <p className="text-sm text-[#475569] mt-1">{description}</p>}
        </div>
        <Button onClick={openCreate} className="bg-[#0EA5E9] hover:bg-[#0284C7] shrink-0" data-testid={`${testid}-add-button`}>
          <Plus size={18} className="mr-1" /> Add {singular}
        </Button>
      </div>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-0 divide-y divide-[#E2E8F0]">
          {list.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 p-4" data-testid={`${testid}-row-${item.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{item.name}</div>
                {subtitle && subtitle(item) && <div className="text-xs text-[#475569] truncate mt-0.5">{subtitle(item)}</div>}
                {body && body(item) && <div className="text-sm text-[#475569] mt-1 line-clamp-2">{body(item)}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {showInactive && (
                  <Badge className={item.active === false
                    ? "bg-[#F1F5F9] text-[#475569] border-0"
                    : "bg-[#16A34A]/10 text-[#16A34A] border-0"}
                    data-testid={`${testid}-status-${item.id}`}>
                    {item.active === false ? "Inactive" : "Active"}
                  </Badge>
                )}
                {meta && meta(item) && <Badge className="bg-[#F1F5F9] text-[#475569] border-0">{meta(item)}</Badge>}
                <Button variant="outline" size="sm" onClick={() => openEdit(item)} data-testid={`${testid}-edit-${item.id}`}>
                  <PencilSimple size={14} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDel(item)} data-testid={`${testid}-delete-${item.id}`}
                        className="text-[#DC2626] hover:text-[#DC2626]">
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
          {list.length === 0 && (
            <div className="p-8 text-center text-sm text-[#475569]" data-testid={`${testid}-empty`}>
              {emptyText || `No ${title.toLowerCase()} yet.`}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{editId ? `Edit ${singular.toLowerCase()}` : `New ${singular.toLowerCase()}`}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">{editId ? "Edit" : "Create"} a {singular.toLowerCase()} record.</DialogDescription>
          <div className="space-y-4 py-1">
            {fields.map((f) => (
              <div key={f.name}>
                <Label className="small-label">{f.label}{f.required && " *"}</Label>
                {renderField(f)}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid={`${testid}-save`}>
              {busy ? "Saving…" : editId ? "Save changes" : `Add ${singular.toLowerCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="max-w-md" data-testid={`${testid}-delete-dialog`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#DC2626]"><Trash size={20} /> Remove {singular.toLowerCase()}</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">
            Remove <span className="font-semibold">{del?.name}</span>? This deactivates it. Existing records that reference it are not changed.
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)}>Cancel</Button>
            <Button onClick={doDelete} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid={`${testid}-confirm-delete`}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SettingsCrud;
